import { TreeViewDataItem, ICategorizeOption, IOptions, TagAttributeValue, IDropDownData, IProductionDropdownData } from './../../store/sidebar/types';
import { ITagAttribute } from 'store/sidebar/types';
import sortAlphabetically from 'helpers/functions/sortAlphabetically';
import { datasetOptionsMappingEmissions, datasetsIds, assetTypeGroupId, assetTypeGroupName, SHARED_ASSET_TYPE_ID } from 'helpers/constants/commons';
import { Parameter } from 'api/propertyCalculator/types';
import { FILTER_TYPES } from 'helpers/constants/filters';

export const createFiltersSubMenues = (response: ITagAttribute[], userCalculations?: Parameter[]) => {
    const filtersTree: TreeViewDataItem[] = [];
    response = response.map(item=>item.group === "Emissions Data" ? {...item, identifierFieldType: 4} : item)
    response = response.filter(item=>item.group !== "No filter") // TODO: Check if this can be handled in the backend NB 2024-04-22
    response = response.filter(item=>item.category != 5) // TODO: Check filter logic here for preventing Production parameters
    
    response.forEach((f: ITagAttribute) => {
        const current = filtersTree.find((filterFromTree: TreeViewDataItem) => filterFromTree.name?.trim() === f.group?.trim())

        if (!current && f.group)
            filtersTree.push({
                name: f.group.trim(),
                category: '',
                element: f.key,
                type: 'subMenu',
                id: f.id,
                identifierFieldType: f.identifierFieldType,
                items: [],
                expanded: false
            });
    })
    
    const result = createFilters(sortAlphabetically(filtersTree, 'name'), response);
    
    if (userCalculations && userCalculations.length > 0) {
        addUserCalculationToAssetTypes(result, userCalculations);
    }
    
    return result;
}

const mapAssetTypeToFilterTreeName = (assetType: string): string => {
    const assetTypeMapping: Record<string, string> = {
        'CO2 Emitters': 'Emissions',
        'Infrastructure': 'Infrastructure Assets',
        'Subsurface Assets': 'Subsurface Assets',
        'Infrastructure Assets': 'Infrastructure Assets',
        'Emissions': 'Emissions',
        'CCS Projects': 'CCS Projects',
        'Blocks': 'Blocks',
        'Licences': 'Licences',
        'Windfarms': 'Windfarms',
    };
    
    return assetTypeMapping[assetType] || assetType;
};

const addUserCalculationToAssetTypes = (filtersTree: TreeViewDataItem[], parameters: Parameter[]): void => {
    if (!parameters || parameters.length === 0) return;
    
    const groupedByAssetType = parameters.reduce((acc, param) => {
        const originalAssetType = param.assetType || 'Other';
        const assetType = mapAssetTypeToFilterTreeName(originalAssetType);
        
        if (!acc[assetType]) {
            acc[assetType] = [];
        }
        acc[assetType].push(param);
        return acc;
    }, {} as Record<string, Parameter[]>);
    
    Object.keys(groupedByAssetType).forEach(assetType => {
        const parametersForAssetType = groupedByAssetType[assetType];
        
        const parameterItems: TreeViewDataItem[] = parametersForAssetType.map(param => ({
            element: 'userCalculation',
            type: 'numeric',
            name: param.parameterName,
            tooltip: param.comment || param.expression,
            items: [],
            values: [],
            id: param.id,
            filterApplied: false,
            firstConditionList: { id: 5, name: 'Is greater than', defaultPriority: 1 },
            firstConditionInput: null,
            logicalOperationList: { id: 1, name: 'And', defaultPriority: 1 },
            secondConditionList: { id: 3, name: 'Is less than', defaultPriority: 2 },
            secondConditionInput: null,
        }));
        
        const userCalculationNode: TreeViewDataItem = {
            name: 'User calculation',
            category: '',
            element: 'userCalculation',
            type: 'subMenu' as keyof typeof FILTER_TYPES,
            id: `user-calculation-${assetType}`,
            items: sortAlphabetically(parameterItems, 'name'),
            expanded: false
        };
        
        const assetTypeNode = filtersTree.find(node => node.name === assetType);
        
        if (assetTypeNode) {
            assetTypeNode.items.push(userCalculationNode);
        } else {
            const newAssetTypeNode: TreeViewDataItem = {
                name: assetType,
                category: '',
                element: assetType,
                type: 'subMenu' as keyof typeof FILTER_TYPES,
                id: `asset-type-${assetType}`,
                items: [userCalculationNode],
                expanded: false
            };
            filtersTree.push(newAssetTypeNode);
        }
    });
}

export const createFilters = (filters: TreeViewDataItem[], response: ITagAttribute[]): TreeViewDataItem[] => {
    
    response.forEach((f: ITagAttribute) => {
        // Determine if this is a numerical field based on unitOfMeasurement
        const isNumericField = f.unitOfMeasurement && f.unitOfMeasurement !== 'TEXT';
        
        // Context-aware defaults based on field type
        const defaultFirstCondition = isNumericField 
            ? { id: 5, name: 'Is greater than', defaultPriority: 1 }
            : { id: 3, name: 'Contains', defaultPriority: 1 };
        const defaultSecondCondition = isNumericField
            ? { id: 3, name: 'Is less than', defaultPriority: 2 }
            : { id: 5, name: 'Is contained In', defaultPriority: 2 };
            
        const filter: TreeViewDataItem = {
            element: f.type === 1 ? 'filter' : 'numeric',
            type: f.type === 1 ? 'filter' : 'numeric',
            firstConditionList: defaultFirstCondition,
            firstConditionInput: null,
            logicalOperationList: { id: 1, name: 'And', defaultPriority: 1 },
            secondConditionList: defaultSecondCondition,
            secondConditionInput: null,
            filterApplied: false,
            tooltip: f.description,
            items: [],
            values: [],
            id: f.id,
            name: f.name,
        };

        filters?.find((e: TreeViewDataItem) => {
            if (e.name && f.group) {
                if (e.name.trim() === f.group.trim()) {
                    const index = e.items.findIndex(k => k.id === f.id)
                    if (index > -1) {
                        e.items.splice(index, 1, filter)
                    } else {
                        e.items?.push(filter);
                    }
                };
            };
            e.items = sortAlphabetically(e.items, 'name');
        });
    })

    // TODO: Review Add Basics logic. This is not required in Categorise By tree. NB 2024-04-22
    const basicsIndex = filters.findIndex(
      (item) => assetTypeGroupId(item.identifierFieldType as number) === SHARED_ASSET_TYPE_ID,
    )
    let basics
    if (basicsIndex > -1) {
        basics = filters[basicsIndex]
        const addBasic: TreeViewDataItem[] = response.filter(item => assetTypeGroupId(item.identifierFieldType as number) === SHARED_ASSET_TYPE_ID).map(f => {
            // Determine if this is a numerical field based on unitOfMeasurement
            const isNumericField = f.unitOfMeasurement && f.unitOfMeasurement !== 'TEXT';
            
            // Context-aware defaults based on field type
            const defaultFirstCondition = isNumericField 
                ? { id: 5, name: 'Is greater than', defaultPriority: 1 }
                : { id: 3, name: 'Contains', defaultPriority: 1 };
            const defaultSecondCondition = isNumericField
                ? { id: 3, name: 'Is less than', defaultPriority: 2 }
                : { id: 5, name: 'Is contained In', defaultPriority: 2 };
                
            return {
                element: f.type === 1 ? 'filter' : 'numeric',
                type: f.type === 1 ? 'filter' : 'numeric',
                firstConditionList: defaultFirstCondition,
                firstConditionInput: null,
                logicalOperationList: { id: 1, name: 'And', defaultPriority: 1 },
                secondConditionList: defaultSecondCondition,
                secondConditionInput: null,
                filterApplied: false,
                tooltip: f.description,
                unit: f.unitOfMeasurement,
                items: [],
                values: [],
                id: f.id,
                name: f.name,
            };
        });
        basics.items = [...addBasic]
        filters.splice(basicsIndex, 1)
    }

    const result: TreeViewDataItem[] = [];

    // Create a map to group items by 'identifierFieldType'
    const groupedMap = new Map();
    datasetsIds.filter(item => item != 5).forEach(item => groupedMap.set(item, []))

    filters.forEach(item => {
        const { name, id, identifierFieldType } = item;
        groupedMap.forEach((items, key, map) => {
            
            if (identifierFieldType === key) {
                const keyToStore = key
                if (!groupedMap.has(key)) {
                    groupedMap.set(key, []);
                }
                groupedMap.get(keyToStore).push(item);
            }
        })
    });

    // Convert the map into the desired format
    groupedMap.forEach((items, key: number, map) => {
        if (datasetOptionsMappingEmissions[key as keyof typeof datasetOptionsMappingEmissions])
            result.push({
                name: datasetOptionsMappingEmissions[key as keyof typeof datasetOptionsMappingEmissions],
                category: '',
                element: datasetOptionsMappingEmissions[key as keyof typeof datasetOptionsMappingEmissions],
                type: 'subMenu',
                id: result.length.toString(),
                items: items,
                expanded: false,
            });
    });
    const final: TreeViewDataItem[] = result
    if (basics) {
        final.unshift(basics)
    }

    if (final.length) {
        return final
    } else {
        return filters
    }
};

export const creteNumericCategorizeOptions = (data: ITagAttribute[]): ICategorizeOption[] => {
    const numericOptions: ICategorizeOption[] = [];

    data.forEach((f: ITagAttribute) => {
        numericOptions.push({
            name: f.name,
            id: f.id,
            value: f.value,
            type: 'numeric',
        })
    })

    return numericOptions
}

export const creteStringCategorizeOptions = (data: ITagAttribute[]): ICategorizeOption[] => {
  const stringOptions: ICategorizeOption[] = []

  data = data.filter(item=>item.group !== "No filter")

  data.forEach((f: ITagAttribute) => {
    stringOptions.push({
      name: f.name,
      id: f.id,
      value: f.value,
      type: "filter",
    })
  })

  return stringOptions
}

export const processQucikPlots = (data: { quickPlots: { id: string; title: string, type: number }[]; }): Array<IOptions> => {
    return data.quickPlots.map((item: { id: string; title: string; type: number }) => {
        return {
            id: item.id,
            name: item.title,
            type: item.type,
        }
    })
}


export const createDropdownModel = (data: TagAttributeValue[]) => 
    data.map((e: TagAttributeValue) => ({
    name: e.name,
    id: e.id,
    value: e.value || e.name,
    key: e.key,
    identifierFieldType: assetTypeGroupName(e.identifierFieldType as number),
    identifierFieldTypeKey: e.identifierFieldType,
    unitOfMeasurement: e.unitOfMeasurement,
    scoringCriteria:e.scoringCriteria,
}))

// Helper function to map asset type to identifierFieldType for Calculator parameters
const mapAssetTypeToIdentifierFieldType = (assetType: string): number => {
    const assetTypeMapping: Record<string, number> = {
        'CO2 Emitters': 4,  // Emissions
        'Emissions': 4,  // Emissions
        'Infrastructure': 2,  // Infrastructure Assets
        'Infrastructure Assets': 2,  // Infrastructure Assets
        'Subsurface Assets': 1,  // Subsurface Assets
        'CCS Projects': 8,  // CCS Projects
        'Blocks': 32,  // Blocks
        'Licences': 64,  // Licences
        'Windfarms': 128,  // Windfarms
    };
    
    return assetTypeMapping[assetType] || 0;
};

// Function to add Calculator parameters to categorize dropdown options
export const addCalculatorParametersToDropdown = (
    tagAttributes: any[],
    calculatorParameters: Parameter[]
): any[] => {
    if (!calculatorParameters || calculatorParameters.length === 0) {
        return tagAttributes;
    }
    
    // Convert calculator parameters to the dropdown model format
    const calculatorDropdownItems = calculatorParameters.map(param => ({
        name: param.parameterName,
        id: param.id,
        value: param.parameterName,
        key: 'userCalculation',
        identifierFieldType: mapAssetTypeToFilterTreeName(param.assetType || 'Other'),
        identifierFieldTypeKey: mapAssetTypeToIdentifierFieldType(param.assetType || 'Other'),
        unitOfMeasurement: 'NUMERIC',
        isCalculatorParameter: true,
        tooltip: param.comment || param.expression,
    }));
    
    // Combine tag attributes with calculator parameters
    return [...tagAttributes, ...calculatorDropdownItems];
}

export const createDropdownModelProduction = (data: (IProductionDropdownData)[]) => data.map((e: IProductionDropdownData) => ({
    name: e.name,
    id: e.id,
    value: e.value || e.name,
    key: e.key,
    labelName: e.labelName,
    unitOfMeasurement: e.unitOfMeasurement,
    type: e.type,
    identifierFieldType: assetTypeGroupName(e.type as number),
    identifierFieldTypeKey: e.type
}))

export const processProductionPlotIndentifierOptions = (data: (IDropDownData & { labelName: string, identifierFieldId: string, isUserData?: boolean })[]): (IDropDownData & { labelName: string, id?: string, isUserData?: boolean })[] => data.map(item => ({ ...item, id: item.identifierFieldId }))